/**
 * CardPage — stripped-chrome page that renders a single ScoutDex card
 * driven by the URL rather than a chat conversation.
 *
 * Each share-URL route (e.g. /p/swampert, /m/shadow-ball) wraps this
 * component, passing in a synthesized question that gets sent to the
 * existing /ask SSE pipeline.  No input bar, no chat thread — just
 * the card, the spoken answer text, a small "Ask Scout anything →"
 * CTA back to /, and the standard sources/credits.
 *
 * A2 of Phase A — Shareable Card URLs.  Routes that USE this component
 * land in A3 onwards.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { League, VisualPayload } from "../types";
import { useSessionStore } from "../store/sessionStore";
import { useAsk } from "../hooks/useAsk";
import CardRenderer from "../components/cards/CardRenderer";
import Header from "../components/Header";

interface CardPageProps {
  /** The synthesized question sent to /ask.  Computed from URL params
   * by the route-specific wrapper (e.g. "Tell me about Swampert"). */
  question: string;
  /** Optional league context — passed through to /ask so league-scoped
   * intents (league_ranking, xl_candy) don't have to re-extract it
   * from the question text. */
  league?: League | null;
  /** Stable key used to retrigger the fetch when the URL changes
   * (different /p/<pokemon> route).  Defaults to `question`. */
  fetchKey?: string;
  /** Phase B share URLs only — pins season-intent queries to a specific
   * gbl_season row by slug (e.g. "memories-in-motion").  Backend
   * dispatcher honors it for the 5 season intents and ignores it
   * otherwise, so it's always safe to set when the route knows a slug. */
  seasonSlug?: string | null;
}

/**
 * The page mounts → triggers /ask once → renders the resulting card.
 *
 * We intentionally don't render the streaming voice text token-by-token
 * the way Chat.tsx does — for a single-card share view the user wants
 * the card to appear once it's ready, not watch it stream in.  Voice
 * text is captured but only rendered after the stream finishes.
 */
export default function CardPage({ question, league = null, fetchKey, seasonSlug = null }: CardPageProps) {
  const { sessionId, initSession } = useSessionStore();
  const { ask, isAsking } = useAsk();

  const [visual, setVisual] = useState<VisualPayload | null>(null);
  const [voiceText, setVoiceText] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  // Token buffer captured during streaming; flushed to voiceText on done.
  const tokenBufRef = useRef("");
  // Guard against StrictMode double-mount issuing the request twice
  // (React 18 dev-mode replays effect bodies once on mount; without
  // this guard the LLM bill doubles in development).
  const askedKeyRef = useRef<string | null>(null);

  // Boot session
  useEffect(() => { void initSession(); }, [initSession]);

  // Fire the request once we have a session.  The effect re-runs when
  // fetchKey changes (e.g. user navigates /p/swampert → /p/garchomp
  // without a full page reload — React Router stays mounted).
  const effectiveKey = fetchKey ?? question;
  const triggerAsk = useCallback(async () => {
    if (!sessionId || askedKeyRef.current === effectiveKey) return;
    askedKeyRef.current = effectiveKey;
    tokenBufRef.current = "";
    setVisual(null);
    setVoiceText("");
    setError(null);

    const messageId = crypto.randomUUID();
    await ask({
      question,
      sessionId,
      messageId,
      callbacks: {
        // onToken — accumulate, render once stream finishes
        onToken: (token) => { tokenBufRef.current += token; },
        // onVisual — show the card as soon as it lands (typically arrives before tokens finish)
        onVisual: (v) => setVisual(v),
        // onDone — flush voice text
        onDone: () => setVoiceText(tokenBufRef.current),
        onError: (msg) => setError(msg),
        // onCorrection — not relevant for share URLs (no voice input here)
      },
      league,
      voiceInput: false,
      seasonSlug,   // Phase B: pin to specific gbl_season row when set
    });
  }, [sessionId, effectiveKey, ask, question, league, seasonSlug]);

  useEffect(() => { void triggerAsk(); }, [triggerAsk]);

  return (
    <>
      <Header league={league} onLeagueChange={() => { /* no-op — share page is read-only */ }} />

      <main className="flex-1 overflow-y-auto overflow-x-hidden pb-20">
        <div className="max-w-2xl mx-auto px-4 pt-6 pb-12">
          {/* Loading skeleton — single placeholder block that disappears
              once the visual arrives.  Don't render multiple stages of
              progress; the share URL recipient should see "the card
              is loading" → "here's the card", not a chat-style status
              line that's only meaningful in a back-and-forth context. */}
          {!visual && !error && isAsking && (
            <div
              data-testid="card-page-skeleton"
              className="bg-scout-elevated rounded-2xl p-4 border border-white/[0.08] animate-pulse"
            >
              <div className="h-3 bg-white/10 rounded w-1/3 mb-4" />
              <div className="space-y-2.5">
                <div className="h-4 bg-white/10 rounded w-3/4" />
                <div className="h-4 bg-white/10 rounded w-1/2" />
                <div className="h-4 bg-white/10 rounded w-2/3" />
              </div>
              <div className="h-2 bg-white/[0.06] rounded w-1/4 mt-6" />
            </div>
          )}

          {/* Error state — clear, retry-able */}
          {error && (
            <div
              data-testid="card-page-error"
              className="bg-scout-elevated rounded-2xl p-6 border border-red-500/20 text-center"
            >
              <p className="text-sm text-white/80 mb-4">{error}</p>
              <button
                type="button"
                onClick={() => {
                  askedKeyRef.current = null;
                  void triggerAsk();
                }}
                className="text-xs text-scout-blue hover:underline underline-offset-2"
              >
                Try again
              </button>
            </div>
          )}

          {/* The card itself */}
          {visual && (
            <CardRenderer payload={visual} />
          )}

          {/* Spoken answer — shown below the card once streaming completes.
              Reads like Scout's voice-track of the card.  Optional but
              useful: matches what an in-app chat user would hear. */}
          {voiceText && !error && (
            <p
              data-testid="card-page-voice"
              className="text-sm text-white/65 leading-relaxed mt-4 px-1"
            >
              {voiceText}
            </p>
          )}

          {/* CTA back to chat — present on every share page so a curious
              recipient has an obvious path into the full app. */}
          <div className="mt-8 text-center">
            <Link
              to="/"
              className="text-xs text-white/40 hover:text-white/70 underline-offset-2 hover:underline transition-colors"
            >
              Ask Scout anything →
            </Link>
          </div>
        </div>
      </main>
    </>
  );
}
